import {
  BaseSyntheticEvent,
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAlert } from 'react-alert';
import { Control, FormState } from 'react-hook-form';
import I18n from 'i18n-js';

import {
  requestEmailVerification,
  verifyEmail,
} from '@lootboy/lib/actions/UserActions';
import {
  EVerificationCodeField,
  TVerificationCodeValues,
  useVerificationCodeForm,
} from '@lootboy/lib/forms/useVerifivationCodeForm';
import { SECOND_MS } from '@lootboy/lib/time';

import Alert from '~/components/Alert';

import { useURLSearchParams } from '~/hooks/useURLSearchParams';
import { useAppDispatch } from '~/redux/store/useAppDispatch';
import { EMAIL_VERIFICATION_PATH } from '~/routes/paths';
import { parseLootBoyError } from '~/utilities/errors';
import { useAppLocation } from '~/utilities/routing';

const RESEND_VERIFICATION_CODE_COOLDOWN = 60;
export const INCORRECT_CODE_ERROR =
  'Api_UserController_error_email_verification_token_incorrect';

type TVerifyFormState = {
  resendCode: {
    countdownValue: number;
    resend: MouseEventHandler<HTMLButtonElement>;
  };
  form: {
    control: Control<TVerificationCodeValues>;
    isSubmitEnabled: boolean;
    onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
    errors: FormState<TVerificationCodeValues>['errors'];
  };
};

export function useVerifyEmailPopup(): TVerifyFormState {
  // when page is opened from a link from a confirmation email it will have a token param in the URL
  const token = useURLSearchParams('token');
  const location = useAppLocation<typeof EMAIL_VERIFICATION_PATH>();
  const alert = useAlert();
  const dispatch = useAppDispatch();
  const [countdownValue, setCountdownValue] = useState(
    RESEND_VERIFICATION_CODE_COOLDOWN,
  );
  const countdownIntervalId = useRef<NodeJS.Timer>();

  // clear interval if present and start decrementing countdown until it gets to 0
  const startCountdown = useCallback(() => {
    if (countdownIntervalId.current) {
      setCountdownValue(RESEND_VERIFICATION_CODE_COOLDOWN);
      clearInterval(countdownIntervalId.current);
    }
    countdownIntervalId.current = setInterval(() => {
      setCountdownValue((prevValue) => {
        if (prevValue === 1 && countdownIntervalId.current) {
          clearInterval(countdownIntervalId.current);
        }
        return prevValue - 1;
      });
    }, SECOND_MS);
  }, []);

  // the countdown should be started on mount if the code has been requested before navigating to this route
  const hasCodeBeenRequestedOnNavigation = location.state?.isCodeRequested;
  useEffect(() => {
    if (hasCodeBeenRequestedOnNavigation) {
      startCountdown();
    }
  }, [startCountdown, hasCodeBeenRequestedOnNavigation]);

  useEffect(() => {
    return () => {
      if (countdownIntervalId.current) {
        clearInterval(countdownIntervalId.current);
      }
    };
  }, []);

  const requestCode: MouseEventHandler<HTMLButtonElement> = useCallback(
    async (mouseEvent) => {
      const { currentTarget: buttonElement } = mouseEvent;
      buttonElement.disabled = true;
      try {
        await dispatch(requestEmailVerification());
        startCountdown();
      } catch (error) {
        const { message } = parseLootBoyError(error);
        buttonElement.disabled = false;
        alert.error(
          <Alert
            title={I18n.t('EmailVerificationView_request_new_token_failed')}
            body={message}
          />,
        );
      }
    },
    [dispatch, startCountdown, alert],
  );

  const {
    isSubmitEnabled,
    onFormSubmit,
    control,
    setError,
    setValue,
    formState: { errors },
  } = useVerificationCodeForm(
    async ({ [EVerificationCodeField.CODE]: code }) => {
      try {
        await dispatch(verifyEmail(code));
      } catch (error) {
        const { messageId, message } = parseLootBoyError(error);
        if (messageId === INCORRECT_CODE_ERROR) {
          setError(EVerificationCodeField.CODE, { message: messageId });
          return;
        }
        alert.error(
          <Alert
            title={I18n.t('EmailVerificationView_verification_failed')}
            body={message}
          />,
        );
      }
    },
  );

  // if token is present in the URL params set field value to it and trigger validation to make submit button enabled
  useEffect(() => {
    if (token) {
      setValue(EVerificationCodeField.CODE, token, { shouldDirty: true });
    }
  }, [token, setValue]);

  return {
    resendCode: {
      countdownValue,
      resend: requestCode,
    },
    form: {
      control,
      isSubmitEnabled,
      onSubmit: onFormSubmit,
      errors,
    },
  };
}
